<template>
    <div class="pivot-table">
        <div class="pivot-header">
            <div class="dimension-pool">
                <div class="pool-title">📊 拖动维度到下方</div>
                <div class="dimension-chips">
                    <div v-for="dim in availableDimensions" :key="dim.field" 
                         class="dimension-chip" 
                         draggable="true"
                         @dragstart="onDragStart($event, dim)">
                        {{ dim.label }}
                    </div>
                </div>
            </div>
            <div class="pivot-controls">
                <div class="control-row">
                    <label>行维度:</label>
                    <div class="drop-zone" 
                         @drop="onDrop($event, 'row')" 
                         @dragover.prevent
                         @dragenter="onDragEnter($event, 'row')"
                         @dragleave="onDragLeave">
                        <div v-if="!rowDimension" class="drop-placeholder">拖动维度到此</div>
                        <div v-else class="selected-dimension" @click="clearDimension('row')">
                            {{ rowDimension.label }} ×
                        </div>
                    </div>
                </div>
                <div class="control-row">
                    <label>列维度:</label>
                    <div class="drop-zone" 
                         @drop="onDrop($event, 'column')" 
                         @dragover.prevent
                         @dragenter="onDragEnter($event, 'column')"
                         @dragleave="onDragLeave">
                        <div v-if="!columnDimension" class="drop-placeholder">拖动维度到此</div>
                        <div v-else class="selected-dimension" @click="clearDimension('column')">
                            {{ columnDimension.label }} ×
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="pivotData.length > 0" class="pivot-body">
            <table class="pivot-grid">
                <thead>
                    <tr>
                        <th class="corner-cell">{{ rowDimension?.label || '' }} \ {{ columnDimension?.label || '' }}</th>
                        <th v-for="col in columnValues" :key="col" class="col-header">{{ col }}</th>
                        <th class="total-header">合计</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="row in rowValues" :key="row">
                        <td class="row-header">{{ row }}</td>
                        <td v-for="col in columnValues" :key="col" 
                            class="data-cell"
                            :style="getCellStyle(getCellValue(row, col))">
                            {{ formatAmount(getCellValue(row, col)) }}
                        </td>
                        <td class="total-cell">{{ formatAmount(getRowTotal(row)) }}</td>
                    </tr>
                    <tr class="total-row">
                        <td class="row-header">合计</td>
                        <td v-for="col in columnValues" :key="col" class="total-cell">
                            {{ formatAmount(getColumnTotal(col)) }}
                        </td>
                        <td class="grand-total">{{ formatAmount(grandTotal) }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
        <div v-else class="empty-pivot">
            <p>请拖动维度到行和列开始分析</p>
        </div>
    </div>
</template>

<script>
export default {
    name: 'PivotTable',
    props: {
        data: {
            type: Array,
            default: () => []
        },
        dimensions: {
            type: Array,
            default: null
        },
        valueField: {
            type: String,
            default: 'sales_amount'
        }
    },
    data() {
        return {
            defaultDimensions: [
                { field: 'sales_region', label: '销售区域' },
                { field: 'sales_name', label: '销售人员' },
                { field: 'region_type', label: '类型' },
                { field: 'agent_name', label: '代理商' },
                { field: 'agent_id', label: '代理ID' },
                { field: 'bill_month', label: '月份' },
                { field: 'quarter', label: '季度' },
                { field: 'year', label: '年份' }
            ],
            rowDimension: null,
            columnDimension: null,
            draggedDimension: null
        }
    },
    computed: {
        activeDimensions() {
            return this.dimensions || this.defaultDimensions;
        },
        availableDimensions() {
            return this.activeDimensions.filter(d => 
                d !== this.rowDimension && d !== this.columnDimension
            );
        },
        pivotData() {
            if (!this.rowDimension || !this.columnDimension || !this.data.length) return [];
            
            const result = {};
            this.data.forEach(item => {
                const rowKey = item[this.rowDimension.field] || '未知';
                const colKey = item[this.columnDimension.field] || '未知';
                const key = `${rowKey}|||${colKey}`;
                
                if (!result[key]) {
                    result[key] = { row: rowKey, col: colKey, amount: 0 };
                }
                result[key].amount += (item[this.valueField] || 0);
            });
            
            return Object.values(result);
        },
        rowValues() {
            return [...new Set(this.pivotData.map(d => d.row))].filter(v => v).sort();
        },
        columnValues() {
            return [...new Set(this.pivotData.map(d => d.col))].filter(v => v).sort();
        },
        grandTotal() {
            return this.pivotData.reduce((sum, d) => sum + d.amount, 0);
        },
        maxValue() {
            return Math.max(...this.pivotData.map(d => d.amount), 0);
        }
    },
    methods: {
        onDragStart(event, dimension) {
            this.draggedDimension = dimension;
            event.dataTransfer.effectAllowed = 'move';
        },
        onDragEnter(event, target) {
            event.currentTarget.classList.add('drag-over');
        },
        onDragLeave(event) {
            event.currentTarget.classList.remove('drag-over');
        },
        onDrop(event, target) {
            event.currentTarget.classList.remove('drag-over');
            if (!this.draggedDimension) return;
            
            if (target === 'row') {
                this.rowDimension = this.draggedDimension;
            } else {
                this.columnDimension = this.draggedDimension;
            }
            this.draggedDimension = null;
        },
        clearDimension(target) {
            if (target === 'row') {
                this.rowDimension = null;
            } else {
                this.columnDimension = null;
            }
        },
        getCellValue(row, col) {
            const item = this.pivotData.find(d => d.row === row && d.col === col);
            return item ? item.amount : 0;
        },
        getRowTotal(row) {
            return this.pivotData.filter(d => d.row === row).reduce((sum, d) => sum + d.amount, 0);
        },
        getColumnTotal(col) {
            return this.pivotData.filter(d => d.col === col).reduce((sum, d) => sum + d.amount, 0);
        },
        formatAmount(value) {
            if (!value) return '-';
            return '$' + value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
        },
        getCellStyle(value) {
            if (!value || !this.maxValue) return {};
            const intensity = value / this.maxValue;
            return {
                background: `rgba(0, 242, 254, ${intensity * 0.3})`,
                fontWeight: intensity > 0.5 ? 'bold' : 'normal'
            };
        }
    }
}
</script>

<style scoped>
.pivot-table {
    width: 100%;
    height: 100%;
}

.pivot-header {
    margin-bottom: 20px;
}

.dimension-pool {
    background: rgba(0, 242, 254, 0.05);
    border: 1px solid rgba(0, 242, 254, 0.3);
    border-radius: 8px;
    padding: 15px;
    margin-bottom: 15px;
}

.pool-title {
    color: #00f2fe;
    font-size: 14px;
    margin-bottom: 10px;
    font-weight: bold;
}

.dimension-chips {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

.dimension-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 8px 15px;
    background: linear-gradient(135deg, rgba(0, 242, 254, 0.2), rgba(79, 172, 254, 0.2));
    border: 1px solid rgba(0, 242, 254, 0.5);
    border-radius: 20px;
    color: #00f2fe;
    cursor: move;
    user-select: none;
    transition: all 0.3s;
    font-size: 13px;
}

.dimension-chip:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 242, 254, 0.4);
}

.pivot-controls {
    display: flex;
    gap: 20px;
}

.control-row {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 10px;
}

.control-row label {
    color: #00f2fe;
    font-size: 13px;
    white-space: nowrap;
}

.drop-zone {
    flex: 1;
    min-height: 45px;
    border: 2px dashed rgba(0, 242, 254, 0.3);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s;
}

.drop-zone.drag-over {
    border-color: #00f2fe;
    background: rgba(0, 242, 254, 0.1);
    box-shadow: 0 0 20px rgba(0, 242, 254, 0.3);
}

.drop-placeholder {
    color: rgba(0, 242, 254, 0.5);
    font-size: 12px;
}

.selected-dimension {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 15px;
    background: rgba(0, 242, 254, 0.2);
    border-radius: 20px;
    color: #00f2fe;
    cursor: pointer;
    font-size: 13px;
}

.selected-dimension:hover {
    background: rgba(0, 242, 254, 0.3);
}

.pivot-body {
    overflow: auto;
    max-height: 400px;
}

.pivot-grid {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
}

.pivot-grid th,
.pivot-grid td {
    padding: 10px;
    border: 1px solid rgba(0, 242, 254, 0.2);
    text-align: center;
}

.corner-cell {
    background: linear-gradient(135deg, rgba(0, 242, 254, 0.3), rgba(79, 172, 254, 0.3));
    color: #00f2fe;
    font-weight: bold;
    position: sticky;
    left: 0;
    top: 0;
    z-index: 3;
}

.col-header {
    background: rgba(0, 242, 254, 0.2);
    color: #00f2fe;
    font-weight: bold;
    position: sticky;
    top: 0;
    z-index: 2;
}

.row-header {
    background: rgba(0, 242, 254, 0.15);
    color: #00f2fe;
    font-weight: bold;
    position: sticky;
    left: 0;
    z-index: 1;
}

.data-cell {
    color: #fff;
    transition: all 0.3s;
}

.data-cell:hover {
    transform: scale(1.05);
    box-shadow: 0 0 15px rgba(0, 242, 254, 0.5);
}

.total-header,
.total-cell {
    background: rgba(240, 147, 251, 0.2);
    color: #f093fb;
    font-weight: bold;
}

.grand-total {
    background: linear-gradient(135deg, rgba(0, 242, 254, 0.3), rgba(240, 147, 251, 0.3));
    color: #00ff88;
    font-weight: bold;
    font-size: 14px;
}

.total-row {
    position: sticky;
    bottom: 0;
}

.empty-pivot {
    text-align: center;
    padding: 60px 20px;
    color: rgba(0, 242, 254, 0.5);
}

.empty-pivot p {
    font-size: 14px;
    margin: 0;
}
</style>
